// Utility functions to categorize symbols as Stocks, ETFs, or Indices
// Uses major_tickers_all.json for comprehensive symbol lists

import majorTickersAll from '../major_tickers_all.json';

// Create Sets from the JSON data for fast lookup
const stocks = new Set(
  majorTickersAll.stocks.map((s) => String(s ?? '').trim().toUpperCase()).filter(Boolean)
);
const etfs = new Set(
  majorTickersAll.etfs.map((s) => String(s ?? '').trim().toUpperCase()).filter(Boolean)
);
const indices = new Set(
  majorTickersAll.indices.map((s) => String(s ?? '').trim().toUpperCase()).filter(Boolean)
);

// Index patterns (symbols that match these patterns are indices)
const INDEX_PATTERNS = [
  /^\^[A-Z0-9]+$/,  // ^SPX, ^NDX, ^GSPC, ^VIX
  /^I:[A-Z0-9]+$/,  // I:SPX, I:NDX
  /^\$[A-Z0-9.]+\.X$/,  // $SPXW.X format
];

/**
 * Get the category of a symbol (Stock, ETF, or Index)
 * Priority: Stocks > ETFs > Indices (to handle symbols that appear in multiple lists)
 */
export function getSymbolCategory(symbol: string): 'stock' | 'etf' | 'index' | 'unknown' {
  const upperSymbol = String(symbol ?? '').trim().toUpperCase();
  if (!upperSymbol) return 'unknown';
  
  // Check if it's in the stocks list first (highest priority)
  if (stocks.has(upperSymbol)) return 'stock';
  
  // Check if it's in the ETFs list (before indices, since ETFs are tradeable instruments)
  if (etfs.has(upperSymbol)) return 'etf';
  
  // Check if it's in the indices list
  if (indices.has(upperSymbol)) return 'index';
  
  // Check index patterns (for symbols like ^SPX, I:SPX, $SPXW.X)
  for (const pattern of INDEX_PATTERNS) {
    if (pattern.test(upperSymbol)) return 'index';
  }
  
  // Default: assume stock if not found (most symbols are stocks)
  return 'stock';
}

/**
 * Check if a symbol matches the selected category filter
 */
export function matchesCategoryFilter(
  symbol: string, 
  categoryFilter: 'all' | 'stocks' | 'etfs' | 'indices'
): boolean {
  if (categoryFilter === 'all') return true;
  
  const category = getSymbolCategory(String(symbol ?? ''));
  
  switch (categoryFilter) {
    case 'stocks':
      return category === 'stock';
    case 'etfs':
      return category === 'etf';
    case 'indices':
      return category === 'index';
    default:
      return true;
  }
}

/**
 * Get category from underlying symbol in data item
 */
export function getUnderlyingCategory(item: any): 'stock' | 'etf' | 'index' | 'unknown' {
  const underlying = item?.Underlying || item?.underlyingSymbol || item?.underlying_symbol || '';
  return getSymbolCategory(underlying);
}

